/**
 * User Metrics raw-payload parsing.
 *
 * Turns the raw S3 export (V1 flat or V2 grouped) into the flat, typed
 * UserMetricsParsedData rows every other module in this feature consumes.
 * Nothing downstream should have to know which payload shape it came from.
 */
import moment from 'moment';
import { Metadata, UserMetricsParsedData, UserMetricsParsedDetails, UserMetricsRawData } from '../UserMetricsInterfaces';
import { inflateUserMetricsPayload } from '../UserMetricsInflation';
import { getLastFewMonthsForUserMetrics } from 'src/utilities/DateTimeUtilities';
import { ALL_APPLICATIONS, FilterDateFormat_MMM_YYYY, OEEmptyStringMappings } from '../../FinTechOpsConstants';

export function filterDataByCreateDates(rawData: UserMetricsRawData[]): UserMetricsRawData[] {
  const currentDate = moment();
  const userMetricsStartMonth = getLastFewMonthsForUserMetrics();

  const included: UserMetricsRawData[] = [];

  // log_month has only a handful of distinct values, so cache the in-window decision
  // per value rather than constructing a moment for every record.
  const inWindowCache = new Map<string, boolean>();

  rawData.forEach((item) => {
    let isInWindow = inWindowCache.get(item.log_month);
    if (isInWindow === undefined) {
      isInWindow = moment(item.log_month, 'YYYY-MM-DD').isBetween(userMetricsStartMonth, currentDate, 'day', '[]');
      inWindowCache.set(item.log_month, isInWindow);
    }
    if (isInWindow) {
      included.push(item);
    }
  });

  return included;
}

/**
 * Backfills missing user hierarchy data across raw records for the same user_login.
 *
 * Problem: Due to data refresh timing differences, some records for a user_login may
 * have null/empty values for hierarchy fields (user_manager_login_hierarchy,
 * user_manager_name_hierarchy, user_manager_job_level_hierarchy, team_size_rollup)
 * while other records for the same user have valid values.
 *
 * Solution: Single pass through the data with a deferred backfill list.
 * - As we iterate, we build a lookup map of known good hierarchy values per user_login.
 * - Records missing values are filled immediately if we've already seen good data for that user.
 * - Records seen before good data is found are added to a deferred list.
 * - After the pass, the deferred list is resolved using the now-populated lookup map.
 *
 * This mutates the input array in place for performance.
 *
 * @param rawData - Array of raw user metrics data to backfill (mutated in place)
 * @returns The same array, with hierarchy nulls filled where possible
 */
export const backfillUserHierarchyData = (rawData: UserMetricsRawData[]): UserMetricsRawData[] => {
  // Map of user_login -> known good hierarchy values
  const knownHierarchies = new Map<
    string,
    {
      user_manager_login_hierarchy: string;
      user_manager_name_hierarchy: string;
      user_manager_job_level_hierarchy: string;
      team_size_rollup: string;
    }
  >();

  // Indices of records that were seen before we found good values for their user_login
  const deferredIndices: number[] = [];

  const hasValidHierarchy = (item: UserMetricsRawData): boolean => {
    return !!(
      item.user_manager_login_hierarchy &&
      item.user_manager_name_hierarchy &&
      item.user_manager_job_level_hierarchy &&
      item.team_size_rollup
    );
  };

  const isMissingHierarchy = (item: UserMetricsRawData): boolean => {
    return (
      !item.user_manager_login_hierarchy || !item.user_manager_name_hierarchy || !item.user_manager_job_level_hierarchy || !item.team_size_rollup
    );
  };

  const applyHierarchy = (
    item: UserMetricsRawData,
    known: {
      user_manager_login_hierarchy: string;
      user_manager_name_hierarchy: string;
      user_manager_job_level_hierarchy: string;
      team_size_rollup: string;
    }
  ): void => {
    if (!item.user_manager_login_hierarchy) item.user_manager_login_hierarchy = known.user_manager_login_hierarchy;
    if (!item.user_manager_name_hierarchy) item.user_manager_name_hierarchy = known.user_manager_name_hierarchy;
    if (!item.user_manager_job_level_hierarchy) item.user_manager_job_level_hierarchy = known.user_manager_job_level_hierarchy;
    if (!item.team_size_rollup) item.team_size_rollup = known.team_size_rollup;
  };

  // Single pass through the data
  for (let i = 0; i < rawData.length; i++) {
    const item = rawData[i];
    const userLogin = item.user_login;

    if (!userLogin) continue;

    const existing = knownHierarchies.get(userLogin);

    if (existing) {
      // We already know good values for this user
      if (isMissingHierarchy(item)) {
        applyHierarchy(item, existing);
      }
    } else {
      // First time seeing this user_login
      if (hasValidHierarchy(item)) {
        // This record has good data - store it
        knownHierarchies.set(userLogin, {
          user_manager_login_hierarchy: item.user_manager_login_hierarchy,
          user_manager_name_hierarchy: item.user_manager_name_hierarchy,
          user_manager_job_level_hierarchy: item.user_manager_job_level_hierarchy,
          team_size_rollup: item.team_size_rollup
        });
      } else {
        // This record is missing data and we haven't seen good data yet - defer it
        deferredIndices.push(i);
      }
    }
  }

  // Resolve deferred records using the now-populated lookup map
  for (const index of deferredIndices) {
    const item = rawData[index];
    const known = knownHierarchies.get(item.user_login);
    if (known) {
      applyHierarchy(item, known);
    }
  }

  return rawData;
};

export const returnUsefulRawUserMetricsData = (userMetricsRawDetails: UserMetricsRawData[], lastUpdatedAt: Metadata): UserMetricsParsedDetails => {
  const returnItems: UserMetricsParsedData[] = [];

  // V2 format detection + inflation: if S3 payload is grouped format, inflate to flat records.
  // This is transparent to all downstream processing — after inflation, data is identical to V1.
  const flatData = inflateUserMetricsPayload(userMetricsRawDetails as unknown[]);

  const last6MonthData = filterDataByCreateDates(flatData);

  // Backfill missing hierarchy data before parsing
  backfillUserHierarchyData(last6MonthData);

  // Filter out "All Applications" rollup rows from the S3 export data.
  // These are aggregate rows that duplicate per-application detail rows and cause double-counting.
  const filteredData = last6MonthData.filter(
    (item) => item.application !== ALL_APPLICATIONS && item.display_sub_application_name !== ALL_APPLICATIONS
  );

  // Month derivations are a pure function of log_month, and there are only ~6 distinct
  // values across the whole payload — so parse each one once instead of building a
  // moment per record (which dominated parse time on large payloads).
  const monthDerivationCache = new Map<string, { create_date_formatted: string; created_on_month: number }>();
  const getMonthDerivations = (logMonth: string) => {
    let derived = monthDerivationCache.get(logMonth);
    if (!derived) {
      const logMonth_YYYY_MM_DD = moment(logMonth, 'YYYY-MM-DD');
      derived = {
        create_date_formatted: logMonth_YYYY_MM_DD.format(FilterDateFormat_MMM_YYYY),
        created_on_month: logMonth_YYYY_MM_DD.month() + 1 // Add 1 to get one-based month number
      };
      monthDerivationCache.set(logMonth, derived);
    }
    return derived;
  };

  filteredData.forEach((item: UserMetricsRawData) => {
    const { create_date_formatted, created_on_month } = getMonthDerivations(item.log_month);

    returnItems.push({
      team_name: item.team_name || OEEmptyStringMappings.Unassigned_Team,

      manager_name_hierarchy_concat: item.supervisor_name_hierarchy
        ? item.supervisor_name_hierarchy.split(':')
        : [OEEmptyStringMappings.Unassigned_Manager],
      manager_login_hierarchy_concat: item.supervisor_login_name_hierarchy
        ? item.supervisor_login_name_hierarchy.split(':')
        : [OEEmptyStringMappings.Unassigned_Login],

      manager_name: item.supervisor_name_hierarchy ? item.supervisor_name_hierarchy.split(':')[0] : OEEmptyStringMappings.Unassigned_Manager,
      manage_login: item.supervisor_login_name_hierarchy
        ? item.supervisor_login_name_hierarchy.split(':')[0]
        : OEEmptyStringMappings.Unassigned_Login,

      application: item.display_sub_application_name || item.application, // Use display name from mapping if available
      application_type: item.application_type,

      user_manager_login_hierarchy: item.user_manager_login_hierarchy
        ? item.user_manager_login_hierarchy.split(':')
        : [OEEmptyStringMappings.Unassigned_Manager_Login],
      user_manager_name_hierarchy: item.user_manager_name_hierarchy
        ? item.user_manager_name_hierarchy.split(':')
        : [OEEmptyStringMappings.Unassigned_Manager],
      user_manager_job_level_hierarchy: item.user_manager_job_level_hierarchy ? item.user_manager_job_level_hierarchy.split(':') : ['-1'],
      team_size_rollup: item.team_size_rollup ? item.team_size_rollup.split(':').map(Number) : [0],

      user_job_level: item.user_job_level || 0,
      user_job_family_description: item.user_job_family_description || 'Unknown',
      user_name: item.user_name || '',

      log_month: item.log_month,
      create_date_formatted,
      created_on_month,

      measure_value: parseInt(item.measure_value?.toString()),
      // Missing column (undefined), empty string, or a non-numeric/garbage string all normalise to
      // undefined — NOT 0 — so downstream "has weeks data" guards (typeof === 'number') stay honest
      // and a month with no real weeks data yields null stickiness rather than a fabricated 0.
      weeks_active_in_month:
        item.weeks_active_in_month == null || item.weeks_active_in_month === '' || !Number.isFinite(Number(item.weeks_active_in_month))
          ? undefined
          : Number(item.weeks_active_in_month),
      user_login: item.user_login,
      active_since: item.active_since,
      last_active: item.last_active,
      user_login_count_distinct: 0, // Will be calculated during aggregation

      job_family: item.user_job_family ? item.user_job_family : 'Unmapped',
      job_family_description: item.user_job_family_description ? item.user_job_family_description : 'Unmapped'
    } as UserMetricsParsedData);
  });

  return {
    metadata: lastUpdatedAt,
    data: returnItems
  } as UserMetricsParsedDetails;
};
